import { Badge, Box, Flex, HStack, IconButton, Text, Tooltip } from "@chakra-ui/react";
import React, { useState } from "react";
import { DevLogRow } from "../../../networking/dev-log.hooks";
import moment from "moment";
import { CopyIcon } from "@chakra-ui/icons";

interface Props {
	value: DevLogRow;
}

const commonBadgeProps = {
	fontSize: "14px",
	fontWeight: "medium",
	lineHeight: 1,
	px: "1.5",
	py: "0.5",
};

const METHOD_COLORS: { [key: string]: string } = {
	GET: "green",
	POST: "blue",
	PUT: "yellow",
	DELETE: "red",
};

const LogRow: React.FC<Props> = ({ value }) => {
	const [expanded, setExpanded] = useState(false);

	function renderStatusBadge(status: number) {
		if (status >= 200 && status < 300) {
			return (
				<Badge colorScheme={"green"} {...commonBadgeProps}>
					{status}
				</Badge>
			);
		}

		if (status >= 400 && status < 500) {
			return (
				<Badge colorScheme={"yellow"} {...commonBadgeProps}>
					{status}
				</Badge>
			);
		}

		if (status >= 500) {
			return (
				<Badge colorScheme={"red"} {...commonBadgeProps}>
					{status}
				</Badge>
			);
		}

		return null;
	}

	function renderMethod(method: string) {
		const bg = METHOD_COLORS[method as keyof typeof METHOD_COLORS];

		return (
			<Badge colorScheme={bg || "gray"} {...commonBadgeProps}>
				{method}
			</Badge>
		);
	}

	function renderUser(userId: string, roles: string[]) {
		return (
			<Text fontSize={"sm"} color={"gray.600"} data-contact={userId}>
				{roles?.join(", ") || ""} {userId.substring(0, 6)}
			</Text>
		);
	}

	return (
		<Box
			borderBottomStyle={"solid"}
			borderBottomWidth={1}
			borderBottomColor={"gray.300"}
			_hover={{ bg: "gray.100" }}
			py="3"
			px="0.5"
			onClick={() => setExpanded(!expanded)}
			cursor={"pointer"}
		>
			<Flex w="full" justifyContent={"space-between"} alignItems={"center"}>
				<HStack alignItems={"center"} spacing={5}>
					{renderStatusBadge(value.status)}
					{renderMethod(value.req.method?.toUpperCase())}
					<Text
						fontFamily={"mono"}
						fontSize={"sm"}
						w="600px"
						textOverflow={"fade"}
						wordBreak={"break-all"}
						noOfLines={1}
					>
						{value.req.path}
					</Text>
					{renderUser(value.userId, value.roles)}
				</HStack>
				<Tooltip label={moment(value.created).toISOString()}>
					<Text fontSize={"sm"}>{moment(value.created).fromNow(true)}</Text>
				</Tooltip>
			</Flex>
			{expanded && (
				<Flex mt="2">
					<Box
						w="50%"
						overflowX={"scroll"}
						borderRightStyle={"solid"}
						borderRightColor={"gray.400"}
						borderRightWidth={1}
						pr="2"
					>
						<HStack alignItems={"center"} mb="2">
							<Text fontSize={"xs"} fontWeight={"medium"} color="gray.600">
								Request
							</Text>
							<IconButton
								icon={<CopyIcon />}
								size={"xs"}
								aria-label="Copy"
								onClick={(e) => {
									e.stopPropagation();
									navigator.clipboard.writeText(JSON.stringify(value.req, null, 2));
								}}
							/>
						</HStack>
						<pre>
							<code style={{ fontSize: 12, lineHeight: 1.1 }}>
								{JSON.stringify(value.req, null, 2)}
							</code>
						</pre>
					</Box>
					<Box w="50%" overflowX={"scroll"} pl="2">
						<HStack alignItems={"center"} mb="2">
							<Text fontSize={"xs"} fontWeight={"medium"} color="gray.600">
								Response
							</Text>
							<IconButton
								icon={<CopyIcon />}
								size={"xs"}
								aria-label="Copy"
								onClick={(e) => {
									e.stopPropagation();
									navigator.clipboard.writeText(JSON.stringify(value.res, null, 2));
								}}
							/>
						</HStack>
						<pre>
							<code style={{ fontSize: 12, lineHeight: 1.1 }}>
								{value.res ? JSON.stringify(value.res, null, 2) : "No response"}
							</code>
						</pre>
					</Box>
				</Flex>
			)}
		</Box>
	);
};

export default LogRow;
